import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const SECTIONS = [
  { id: 'a', label: 'Identificação', icon: '🏢' },
  { id: 'b', label: 'Estratégia', icon: '🎯' },
  { id: 'c', label: 'Governança', icon: '⚖️' },
  { id: 'd', label: 'Social', icon: '👥' },
  { id: 'e', label: 'Ambiental', icon: '🌱' },
  { id: 'f', label: 'Prioridades', icon: '📋' },
]

const SCORE_OPTIONS = [
  { value: '', label: 'Selecione...' },
  { value: 'Sim', label: 'Sim' },
  { value: 'Parcial', label: 'Parcial' },
  { value: 'Nao', label: 'Não' },
]

const MAT_OPTIONS = [
  { value: '', label: 'Selecione maturidade...' },
  { value: '0', label: '0 – Não existe' },
  { value: '1', label: '1 – Informal/Ad hoc' },
  { value: '2', label: '2 – Documentado' },
  { value: '3', label: '3 – Monitorado' },
  { value: '4', label: '4 – Otimizado' },
]

const SETOR_OPTIONS = [
  '', 'Agronegócio', 'Construção Civil', 'Educação', 'Energia',
  'Financeiro / Fintech', 'Indústria', 'Logística / Transporte',
  'Mineração', 'Óleo e Gás', 'Saúde', 'Tecnologia / Software',
  'Telecomunicações', 'Varejo / E-commerce', 'Outro',
]

const FAT_OPTIONS = [
  '', 'Até R$2,4M (MEI/ME)', 'R$2,4M–R$16M', 'R$16M–R$90M (Médio Porte)',
  'R$90M–R$300M', 'Acima de R$300M',
]

const TRIBUT_OPTIONS = [
  '', 'Simples Nacional', 'Lucro Presumido', 'Lucro Real', 'Lucro Arbitrado',
  'Entidade Sem Fins Lucrativos', 'Não sei',
]

const AUD_OPTIONS = [
  '', 'Mensal', 'Trimestral', 'Semestral', 'Anual', 'Não realiza',
]

const FREQ_OPTIONS = [
  '', 'Anual', 'Semestral', 'Trimestral', 'Mensal', 'Nunca',
]

const POL_OPTIONS = [
  'Código de Conduta', 'Política de Privacidade (LGPD)', 'Política de Segurança da Informação',
  'Política de Gestão de Riscos', 'Política ESG/Sustentabilidade',
  'Política de Diversidade & Inclusão', 'Política de Fornecedores',
]

const RH_KPI_OPTIONS = [
  'Turnover', 'Absenteísmo', 'Satisfação (eNPS)', 'Horas de Treinamento',
  'Acidentes de Trabalho', 'Diversidade de Gênero', 'Diversidade Étnico-Racial',
]

const MEDE_OPTIONS = [
  'Energia elétrica', 'Água', 'Resíduos gerados', 'Emissões GEE (Gases de Efeito Estufa)',
  'Efluentes líquidos', 'Resíduos perigosos',
]

type FormData = Record<string, string | number | string[] | undefined>

const STORAGE_KEY = 'nai_esg_diagnostic_draft'

function SelectField({ label, name, options, value, onChange, required, isDark = true }: {
  label: string; name: string; options: string[] | { value: string; label: string }[];
  value: string; onChange: (n: string, v: string) => void; required?: boolean; isDark?: boolean
}) {
  const opts = typeof options[0] === 'string'
    ? (options as string[]).map(o => ({ value: o, label: o || 'Selecione...' }))
    : options as { value: string; label: string }[]
  return (
    <div className="mb-4">
      <label className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 border ${isDark ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"}`}
      >
        {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

function TextField({ label, name, value, onChange, placeholder, required, type = 'text', isDark = true }: {
  label: string; name: string; value: string; onChange: (n: string, v: string) => void;
  placeholder?: string; required?: boolean; type?: string; isDark?: boolean
}) {
  return (
    <div className="mb-4">
      <label className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 border ${isDark ? "bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
      />
    </div>
  )
}

function TextareaField({ label, name, value, onChange, placeholder, rows = 3, isDark = true }: {
  label: string; name: string; value: string; onChange: (n: string, v: string) => void;
  placeholder?: string; rows?: number; isDark?: boolean
}) {
  return (
    <div className="mb-4">
      <label className={`block text-sm font-medium mb-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{label}</label>
      <textarea
        name={name}
        value={value}
        onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 border resize-vertical ${isDark ? "bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
      />
    </div>
  )
}

function CheckboxGroup({ label, name, options, values, onChange, isDark = true }: {
  label: string; name: string; options: string[]; values: string[]; onChange: (n: string, v: string[]) => void; isDark?: boolean
}) {
  const toggle = (opt: string) => {
    const next = values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]
    onChange(name, next)
  }
  return (
    <div className="mb-4">
      <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>{label}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map(opt => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={values.includes(opt)}
              onChange={() => toggle(opt)}
              className={`w-4 h-4 rounded text-emerald-500 focus:ring-emerald-500 ${isDark ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"}`}
            />
            <span className={`text-sm ${isDark ? "text-gray-300 group-hover:text-gray-100" : "text-gray-600 group-hover:text-gray-900"}`}>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function SectionCard({ title, children, isDark = true }: { title: string; children: React.ReactNode; isDark?: boolean }) {
  return (
    <div className={`rounded-xl p-5 mb-4 border transition-colors ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
      <h3 className="text-sm font-semibold text-emerald-500 uppercase tracking-wider mb-4">{title}</h3>
      {children}
    </div>
  )
}

function MaturityField({ label, nameMat, nameEvid, valueMat, valueEvid, onChange, isDark = true }: {
  label: string; nameMat: string; nameEvid: string;
  valueMat: string; valueEvid: string; onChange: (n: string, v: string) => void; isDark?: boolean
}) {
  return (
    <div className={`mb-4 p-4 rounded-lg border ${isDark ? "bg-gray-900/50 border-gray-700" : "bg-gray-100 border-gray-200"}`}>
      <p className={`text-sm font-medium mb-3 ${isDark ? "text-gray-200" : "text-gray-700"}`}>{label}</p>
      <SelectField isDark={isDark} label="Nível de Maturidade" name={nameMat} options={MAT_OPTIONS} value={valueMat} onChange={onChange} />
      <TextField isDark={isDark} label="Evidência / Observação" name={nameEvid} value={valueEvid} onChange={onChange} placeholder="Ex: link, documento, processo..." />
    </div>
  )
}

export default function DiagnosticFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('nai_esg_theme') !== 'light' } catch { return true }
  })

  useEffect(() => {
    try { localStorage.setItem('nai_esg_theme', isDark ? 'dark' : 'light') } catch {}
  }, [isDark])

  const [form, setForm] = useState<FormData>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return {}
  })

  // Auto-save to localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)) } catch { /* ignore */ }
  }, [form])

  const str = (k: string): string => (form[k] as string) || ''
  const arr = (k: string): string[] => (form[k] as string[]) || []

  // postMessage for iframe embed auto-resize
  useEffect(() => {
    const sendHeight = () => {
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'nai-diagnostic-resize', height: document.documentElement.scrollHeight }, '*')
      }
    }
    sendHeight()
    const timer = setTimeout(sendHeight, 300)
    window.addEventListener('resize', sendHeight)
    const observer = new MutationObserver(sendHeight)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true })
    return () => { clearTimeout(timer); window.removeEventListener('resize', sendHeight); observer.disconnect() }
  }, [step])

  // Scroll parent to top on step change
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'nai-diagnostic-scroll-top' }, '*')
    }
  }, [step])

  const set = (name: string, value: string | string[]) => {
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const progress = Math.round(((step + 1) / SECTIONS.length) * 100)

  const handleNext = () => {
    if (step < SECTIONS.length - 1) { setStep(s => s + 1); window.scrollTo(0, 0) }
  }
  const handleBack = () => {
    if (step > 0) { setStep(s => s - 1); window.scrollTo(0, 0) }
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        nome: str('nome'), email: str('email'), cargo: str('cargo'),
        empresa: str('empresa'), local: str('local'), contato: str('contato'),
        whats: str('whats'), filiais: str('filiais'), escopo: str('escopo'),
        setor: str('setor'), fat_faixa: str('fat_faixa'), tribut: str('tribut'),
        colab: form['colab'] ? Number(form['colab']) : null,
        terc: form['terc'] ? Number(form['terc']) : null,
        custos_top3: str('custos_top3'),
        mvv_ano: str('mvv_ano'), metas: str('metas'), processos: str('processos'),
        organograma: str('organograma'), kpis: str('kpis'), riscos_top3: str('riscos_top3'),
        op_top3: str('op_top3'),
        cod_conduta: str('cod_conduta'), cod_conduta_mat: form['cod_conduta_mat'] !== '' && form['cod_conduta_mat'] != null ? Number(form['cod_conduta_mat']) : null,
        cod_conduta_evid: str('cod_conduta_evid'),
        canal_denuncia: str('canal_denuncia'), canal_denuncia_mat: form['canal_denuncia_mat'] !== '' && form['canal_denuncia_mat'] != null ? Number(form['canal_denuncia_mat']) : null,
        canal_denuncia_evid: str('canal_denuncia_evid'),
        pol: arr('pol').length > 0 ? arr('pol') : null, pol_evid: str('pol_evid'),
        reg_mat: form['reg_mat'] !== '' && form['reg_mat'] != null ? Number(form['reg_mat']) : null, reg_evid: str('reg_evid'),
        lgpd_mat: form['lgpd_mat'] !== '' && form['lgpd_mat'] != null ? Number(form['lgpd_mat']) : null, lgpd_evid: str('lgpd_evid'),
        terc_mat: form['terc_mat'] !== '' && form['terc_mat'] != null ? Number(form['terc_mat']) : null, terc_evid: str('terc_evid'),
        dd_mat: form['dd_mat'] !== '' && form['dd_mat'] != null ? Number(form['dd_mat']) : null, dd_evid: str('dd_evid'),
        aud_freq: str('aud_freq'), aud_evid: str('aud_evid'),
        transp_mat: form['transp_mat'] !== '' && form['transp_mat'] != null ? Number(form['transp_mat']) : null, transp_evid: str('transp_evid'),
        cert: str('cert'), cert_quais: str('cert_quais'),
        stake: str('stake'), metas_esg: str('metas_esg'), gov_esg: str('gov_esg'),
        clima: str('clima'), clima_ano: str('clima_ano'),
        rh_kpi: arr('rh_kpi').length > 0 ? arr('rh_kpi') : null, rh_freq: str('rh_freq'),
        treinos: str('treinos'), treinos_desc: str('treinos_desc'),
        sst: str('sst'), sst_evid: str('sst_evid'),
        carreira: str('carreira'), di: str('di'), di_desc: str('di_desc'),
        bemestar: str('bemestar'), jornada: str('jornada'), comunidade: str('comunidade'),
        mede: arr('mede').length > 0 ? arr('mede') : null, dados_onde: str('dados_onde'),
        energia: str('energia'), energia_mat: form['energia_mat'] !== '' && form['energia_mat'] != null ? Number(form['energia_mat']) : null,
        agua: str('agua'), agua_mat: form['agua_mat'] !== '' && form['agua_mat'] != null ? Number(form['agua_mat']) : null,
        efluentes: str('efluentes'), quimicos: str('quimicos'), residuos: str('residuos'),
        lic: str('lic'), desperdicio: str('desperdicio'), gee: str('gee'),
        gee_escopos: str('gee_escopos'), clima_risco: str('clima_risco'), outras: str('outras'),
        expectativas: str('expectativas'), prioridades: str('prioridades'),
        utm_source: searchParams.get('utm_source') || null,
        utm_medium: searchParams.get('utm_medium') || null,
        utm_campaign: searchParams.get('utm_campaign') || null,
        website: '',
      }

      const resp = await fetch('/api/v1/diagnostics/submit?campaign=esg-diagnostico', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error(err.detail || `Erro ${resp.status}`)
      }

      const data = await resp.json()
      localStorage.removeItem(STORAGE_KEY)
      navigate(`/diagnostico-esg/resultado/${data.submission_id}`)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao enviar. Tente novamente.')
      window.scrollTo(0, 0)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b sticky top-0 z-10 transition-colors ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              {isDark ? (
                <img src="https://nai-it.com.br/wp-content/uploads/2025/11/logo_nai_branco.png" alt="NAI IT" style={{ height: "64px" }} className="mb-1" />
              ) : (
                <img src="https://nai-it.com.br/wp-content/uploads/2025/12/NAI-1.svg" alt="NAI IT" style={{ height: "64px" }} className="mb-1" />
              )}
              <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pré-Diagnóstico ESG</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsDark(d => !d)}
                className={`p-2 rounded-lg text-sm transition-colors ${isDark ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title={isDark ? 'Modo claro' : 'Modo escuro'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              <div className="text-right">
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Seção {step + 1} de {SECTIONS.length}</div>
                <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{SECTIONS[step].icon} {SECTIONS[step].label}</div>
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          {/* Step pills */}
          <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
            {SECTIONS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => { setStep(i); window.scrollTo(0, 0) }}
                className={`flex-shrink-0 text-xs px-2 py-1 rounded-full transition-colors ${
                  i === step ? 'bg-emerald-500 text-gray-950 font-semibold' :
                  i < step ? (isDark ? 'bg-gray-700 text-gray-300' : 'bg-emerald-100 text-emerald-700') : (isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-200 text-gray-400')
                }`}
              >
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {error && (
          <div className={`mb-4 p-3 border rounded-lg text-sm ${isDark ? 'bg-red-900/40 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>{error}</div>
        )}

        {/* SECTION A: Identificação */}
        {step === 0 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>🏢 Identificação da Empresa</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Dados básicos para personalizar seu diagnóstico.</p>
            </div>
            <SectionCard isDark={isDark} title="Contato">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <TextField isDark={isDark} label="Nome completo" name="nome" value={str('nome')} onChange={set} required placeholder="Seu nome" />
                <TextField isDark={isDark} label="E-mail corporativo" name="email" value={str('email')} onChange={set} required type="email" placeholder="voce@empresa.com" />
                <TextField isDark={isDark} label="Cargo / Função" name="cargo" value={str('cargo')} onChange={set} placeholder="Ex: Gerente de Sustentabilidade" />
                <TextField isDark={isDark} label="Telefone / WhatsApp" name="whats" value={str('whats')} onChange={set} placeholder="(11) 99999-9999" />
              </div>
            </SectionCard>
            <SectionCard isDark={isDark} title="Empresa">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                <TextField isDark={isDark} label="Razão Social / Nome da Empresa" name="empresa" value={str('empresa')} onChange={set} required placeholder="Nome da empresa" />
                <TextField isDark={isDark} label="Cidade / Estado" name="local" value={str('local')} onChange={set} placeholder="São Paulo, SP" />
                <SelectField isDark={isDark} label="Setor de Atuação" name="setor" options={SETOR_OPTIONS} value={str('setor')} onChange={set} />
                <SelectField isDark={isDark} label="Faixa de Faturamento" name="fat_faixa" options={FAT_OPTIONS} value={str('fat_faixa')} onChange={set} />
                <SelectField isDark={isDark} label="Regime Tributário" name="tribut" options={TRIBUT_OPTIONS} value={str('tribut')} onChange={set} />
                <TextField isDark={isDark} label="Nº de Colaboradores" name="colab" value={str('colab')} onChange={set} type="number" placeholder="Ex: 250" />
                <TextField isDark={isDark} label="Nº de Terceirizados" name="terc" value={str('terc')} onChange={set} type="number" placeholder="Ex: 50" />
                <TextField isDark={isDark} label="Nº de Filiais / Unidades" name="filiais" value={str('filiais')} onChange={set} placeholder="Ex: 3" />
              </div>
              <TextareaField isDark={isDark} label="Escopo principal do negócio" name="escopo" value={str('escopo')} onChange={set} placeholder="Descreva brevemente o que a empresa faz..." />
              <TextareaField isDark={isDark} label="Top 3 Custos da operação" name="custos_top3" value={str('custos_top3')} onChange={set} placeholder="Ex: 1. Folha de pagamento  2. Energia  3. Logística" />
            </SectionCard>
          </div>
        )}

        {/* SECTION B: Estratégia */}
        {step === 1 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>🎯 Estratégia e Gestão</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Avalie a maturidade estratégica da organização.</p>
            </div>
            <SectionCard isDark={isDark} title="Planejamento Estratégico">
              <TextField isDark={isDark} label="Ano do último planejamento estratégico (MVV)" name="mvv_ano" value={str('mvv_ano')} onChange={set} placeholder="Ex: 2024" />
              <SelectField isDark={isDark} label="Possui metas e indicadores formais (KPIs)?" name="metas" options={SCORE_OPTIONS} value={str('metas')} onChange={set} />
              <SelectField isDark={isDark} label="Processos de negócio mapeados e documentados?" name="processos" options={SCORE_OPTIONS} value={str('processos')} onChange={set} />
              <SelectField isDark={isDark} label="Organograma e estrutura hierárquica definidos?" name="organograma" options={SCORE_OPTIONS} value={str('organograma')} onChange={set} />
              <TextareaField isDark={isDark} label="Quais KPIs estratégicos são monitorados?" name="kpis" value={str('kpis')} onChange={set} placeholder="Ex: NPS, CAC, LTV, Turnover..." />
            </SectionCard>
            <SectionCard isDark={isDark} title="Riscos e Oportunidades">
              <TextareaField isDark={isDark} label="Top 3 riscos do negócio" name="riscos_top3" value={str('riscos_top3')} onChange={set} placeholder="Ex: 1. Regulatório  2. Concorrência  3. Clima" />
              <TextareaField isDark={isDark} label="Top 3 oportunidades de crescimento" name="op_top3" value={str('op_top3')} onChange={set} placeholder="Ex: 1. Expansão regional  2. Novo produto  3. ESG como diferencial" />
            </SectionCard>
          </div>
        )}

        {/* SECTION C: Governança */}
        {step === 2 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>⚖️ Governança Corporativa</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Estrutura de conformidade, integridade e transparência.</p>
            </div>
            <SectionCard isDark={isDark} title="Integridade e Compliance">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Código de Conduta / Ética?" name="cod_conduta" options={SCORE_OPTIONS} value={str('cod_conduta')} onChange={set} />
                <SelectField isDark={isDark} label="Maturidade do Código de Conduta" name="cod_conduta_mat" options={MAT_OPTIONS} value={str('cod_conduta_mat')} onChange={set} />
              </div>
              <TextField isDark={isDark} label="Evidência (Código de Conduta)" name="cod_conduta_evid" value={str('cod_conduta_evid')} onChange={set} placeholder="Link, arquivo ou observação" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Canal de Denúncias?" name="canal_denuncia" options={SCORE_OPTIONS} value={str('canal_denuncia')} onChange={set} />
                <SelectField isDark={isDark} label="Maturidade do Canal" name="canal_denuncia_mat" options={MAT_OPTIONS} value={str('canal_denuncia_mat')} onChange={set} />
              </div>
              <TextField isDark={isDark} label="Evidência (Canal de Denúncias)" name="canal_denuncia_evid" value={str('canal_denuncia_evid')} onChange={set} placeholder="Link, arquivo ou observação" />
            </SectionCard>
            <SectionCard isDark={isDark} title="Políticas Corporativas">
              <CheckboxGroup isDark={isDark} label="Políticas existentes na organização:" name="pol" options={POL_OPTIONS} values={arr('pol')} onChange={(n, v) => setForm(p => ({ ...p, [n]: v }))} />
              <TextField isDark={isDark} label="Evidência das políticas" name="pol_evid" value={str('pol_evid')} onChange={set} placeholder="Sistema, pasta, link..." />
            </SectionCard>
            <SectionCard isDark={isDark} title="Conformidade Regulatória">
              <MaturityField isDark={isDark} label="Gestão Regulatória (leis, normas setoriais)" nameMat="reg_mat" nameEvid="reg_evid" valueMat={str('reg_mat')} valueEvid={str('reg_evid')} onChange={set} />
              <MaturityField isDark={isDark} label="Conformidade com LGPD (Privacidade)" nameMat="lgpd_mat" nameEvid="lgpd_evid" valueMat={str('lgpd_mat')} valueEvid={str('lgpd_evid')} onChange={set} />
              <MaturityField isDark={isDark} label="Gestão de Terceiros e Fornecedores" nameMat="terc_mat" nameEvid="terc_evid" valueMat={str('terc_mat')} valueEvid={str('terc_evid')} onChange={set} />
              <MaturityField isDark={isDark} label="Due Diligence de Parceiros" nameMat="dd_mat" nameEvid="dd_evid" valueMat={str('dd_mat')} valueEvid={str('dd_evid')} onChange={set} />
            </SectionCard>
            <SectionCard isDark={isDark} title="Auditoria e Transparência">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Frequência de Auditorias Internas" name="aud_freq" options={AUD_OPTIONS} value={str('aud_freq')} onChange={set} />
                <TextField isDark={isDark} label="Evidência (Auditoria)" name="aud_evid" value={str('aud_evid')} onChange={set} placeholder="Relatório, sistema..." />
              </div>
              <MaturityField isDark={isDark} label="Transparência e Relatórios Externos" nameMat="transp_mat" nameEvid="transp_evid" valueMat={str('transp_mat')} valueEvid={str('transp_evid')} onChange={set} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Certificações de conformidade?" name="cert" options={SCORE_OPTIONS} value={str('cert')} onChange={set} />
                <TextField isDark={isDark} label="Quais certificações?" name="cert_quais" value={str('cert_quais')} onChange={set} placeholder="ISO 9001, 27001, SOC2..." />
              </div>
            </SectionCard>
            <SectionCard isDark={isDark} title="ESG na Governança">
              <SelectField isDark={isDark} label="Engajamento com Partes Interessadas (Stakeholders)?" name="stake" options={SCORE_OPTIONS} value={str('stake')} onChange={set} />
              <SelectField isDark={isDark} label="Metas ESG formais definidas?" name="metas_esg" options={SCORE_OPTIONS} value={str('metas_esg')} onChange={set} />
              <SelectField isDark={isDark} label="Governança ESG estruturada (comitê, responsável)?" name="gov_esg" options={SCORE_OPTIONS} value={str('gov_esg')} onChange={set} />
            </SectionCard>
          </div>
        )}

        {/* SECTION D: Social */}
        {step === 3 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>👥 Dimensão Social</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Práticas de pessoas, saúde, diversidade e comunidade.</p>
            </div>
            <SectionCard isDark={isDark} title="Clima e Cultura Organizacional">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Realiza pesquisa de clima?" name="clima" options={SCORE_OPTIONS} value={str('clima')} onChange={set} />
                <TextField isDark={isDark} label="Ano da última pesquisa de clima" name="clima_ano" value={str('clima_ano')} onChange={set} placeholder="Ex: 2024" />
              </div>
              <CheckboxGroup isDark={isDark} label="KPIs de RH monitorados:" name="rh_kpi" options={RH_KPI_OPTIONS} values={arr('rh_kpi')} onChange={(n, v) => setForm(p => ({ ...p, [n]: v }))} />
              <SelectField isDark={isDark} label="Frequência de monitoramento de RH" name="rh_freq" options={FREQ_OPTIONS} value={str('rh_freq')} onChange={set} />
            </SectionCard>
            <SectionCard isDark={isDark} title="Desenvolvimento e Saúde">
              <SelectField isDark={isDark} label="Programa de treinamento e desenvolvimento?" name="treinos" options={SCORE_OPTIONS} value={str('treinos')} onChange={set} />
              <TextareaField isDark={isDark} label="Descreva os principais programas de capacitação" name="treinos_desc" value={str('treinos_desc')} onChange={set} placeholder="Ex: Trilhas de aprendizagem, e-learning, cursos técnicos..." />
              <SelectField isDark={isDark} label="Programa de SST (Saúde e Segurança no Trabalho)?" name="sst" options={SCORE_OPTIONS} value={str('sst')} onChange={set} />
              <TextField isDark={isDark} label="Evidência SST" name="sst_evid" value={str('sst_evid')} onChange={set} placeholder="PCMSO, PPRA, CIPA, certificado..." />
              <SelectField isDark={isDark} label="Plano de carreira e desenvolvimento formal?" name="carreira" options={SCORE_OPTIONS} value={str('carreira')} onChange={set} />
            </SectionCard>
            <SectionCard isDark={isDark} title="Diversidade e Bem-Estar">
              <SelectField isDark={isDark} label="Programa de Diversidade & Inclusão?" name="di" options={SCORE_OPTIONS} value={str('di')} onChange={set} />
              <TextareaField isDark={isDark} label="Descreva iniciativas de D&I" name="di_desc" value={str('di_desc')} onChange={set} placeholder="Ex: grupos de afinidade, metas de representatividade..." rows={2} />
              <SelectField isDark={isDark} label="Programa de bem-estar / qualidade de vida?" name="bemestar" options={SCORE_OPTIONS} value={str('bemestar')} onChange={set} />
              <TextField isDark={isDark} label="Jornada de trabalho e flexibilidade" name="jornada" value={str('jornada')} onChange={set} placeholder="Ex: Home office, banco de horas, flexível..." />
              <TextareaField isDark={isDark} label="Engajamento com comunidade local / ESG Social" name="comunidade" value={str('comunidade')} onChange={set} placeholder="Ex: Instituto, voluntariado, projetos sociais..." rows={2} />
            </SectionCard>
          </div>
        )}

        {/* SECTION E: Ambiental */}
        {step === 4 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>🌱 Dimensão Ambiental</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Gestão de recursos, emissões, resíduos e riscos climáticos.</p>
            </div>
            <SectionCard isDark={isDark} title="Monitoramento de Recursos">
              <CheckboxGroup isDark={isDark} label="Variáveis ambientais que a empresa mede/monitora:" name="mede" options={MEDE_OPTIONS} values={arr('mede')} onChange={(n, v) => setForm(p => ({ ...p, [n]: v }))} />
              <TextareaField isDark={isDark} label="Onde os dados são armazenados?" name="dados_onde" value={str('dados_onde')} onChange={set} placeholder="Ex: planilha Excel, sistema ERP, não registra..." rows={2} />
            </SectionCard>
            <SectionCard isDark={isDark} title="Energia e Água">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Gestão ativa de energia?" name="energia" options={SCORE_OPTIONS} value={str('energia')} onChange={set} />
                <SelectField isDark={isDark} label="Maturidade (Energia)" name="energia_mat" options={MAT_OPTIONS} value={str('energia_mat')} onChange={set} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                <SelectField isDark={isDark} label="Gestão ativa de água?" name="agua" options={SCORE_OPTIONS} value={str('agua')} onChange={set} />
                <SelectField isDark={isDark} label="Maturidade (Água)" name="agua_mat" options={MAT_OPTIONS} value={str('agua_mat')} onChange={set} />
              </div>
            </SectionCard>
            <SectionCard isDark={isDark} title="Resíduos e Conformidade">
              <SelectField isDark={isDark} label="Gestão de efluentes líquidos?" name="efluentes" options={SCORE_OPTIONS} value={str('efluentes')} onChange={set} />
              <SelectField isDark={isDark} label="Gestão de produtos químicos e perigosos?" name="quimicos" options={SCORE_OPTIONS} value={str('quimicos')} onChange={set} />
              <SelectField isDark={isDark} label="Plano de gerenciamento de resíduos sólidos (PGRS)?" name="residuos" options={SCORE_OPTIONS} value={str('residuos')} onChange={set} />
              <SelectField isDark={isDark} label="Licenças ambientais vigentes?" name="lic" options={SCORE_OPTIONS} value={str('lic')} onChange={set} />
              <SelectField isDark={isDark} label="Iniciativas de combate ao desperdício?" name="desperdicio" options={SCORE_OPTIONS} value={str('desperdicio')} onChange={set} />
            </SectionCard>
            <SectionCard isDark={isDark} title="Clima e Emissões">
              <SelectField isDark={isDark} label="Inventário de GEE (Gases de Efeito Estufa)?" name="gee" options={SCORE_OPTIONS} value={str('gee')} onChange={set} />
              <TextField isDark={isDark} label="Escopos do inventário GEE" name="gee_escopos" value={str('gee_escopos')} onChange={set} placeholder="Ex: Escopo 1 e 2 / Todos os 3 escopos" />
              <SelectField isDark={isDark} label="Análise de riscos climáticos para o negócio?" name="clima_risco" options={SCORE_OPTIONS} value={str('clima_risco')} onChange={set} />
              <TextareaField isDark={isDark} label="Outras iniciativas ambientais relevantes" name="outras" value={str('outras')} onChange={set} placeholder="Ex: Energia solar, reflorestamento, economia circular..." rows={2} />
            </SectionCard>
          </div>
        )}

        {/* SECTION F: Prioridades */}
        {step === 5 && (
          <div>
            <div className="mb-6">
              <h2 className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>📋 Prioridades e Expectativas</h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Nos ajude a personalizar suas recomendações.</p>
            </div>
            <SectionCard isDark={isDark} title="Contexto e Motivação">
              <TextareaField
                label="O que motivou a buscar este diagnóstico? Quais são as principais expectativas?"
                name="expectativas"
                value={str('expectativas')}
                onChange={set}
                placeholder="Ex: Requisito de cliente, pressão regulatória, estratégia de crescimento, due diligence, IPO..."
                rows={4}
              />
            </SectionCard>
            <SectionCard isDark={isDark} title="Prioridades de Ação">
              <TextareaField
                label="Quais são as 3 principais áreas onde a empresa mais precisa evoluir em ESG?"
                name="prioridades"
                value={str('prioridades')}
                onChange={set}
                placeholder="Ex: 1. Governança de dados e LGPD&#10;2. Inventário de carbono&#10;3. Programa de diversidade"
                rows={4}
              />
            </SectionCard>
            {/* Summary */}
            <div className={`mt-6 p-4 rounded-xl border ${isDark ? 'bg-emerald-900/20 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'}`}>
              <h3 className="text-emerald-500 font-semibold mb-2">✅ Pronto para enviar!</h3>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Você preencheu {SECTIONS.length} seções do diagnóstico.
                Ao enviar, você receberá instantaneamente um relatório completo com:
              </p>
              <ul className={`mt-2 space-y-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <li>• Score de maturidade ESG por dimensão</li>
                <li>• Radar chart das dimensões avaliadas</li>
                <li>• Obrigações regulatórias identificadas</li>
                <li>• Recomendações personalizadas</li>
                <li>• Link para download do relatório PDF</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className={`flex justify-between mt-6 pt-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <button
            onClick={handleBack}
            disabled={step === 0}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${isDark ? 'text-gray-300 bg-gray-800 hover:bg-gray-700' : 'text-gray-600 bg-gray-200 hover:bg-gray-300'}`}
          >
            ← Anterior
          </button>
          {step < SECTIONS.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-5 py-2.5 text-sm font-medium text-gray-950 bg-emerald-500 hover:bg-emerald-400 rounded-lg transition-colors font-semibold"
            >
              Próxima →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading || !str('nome') || !str('email') || !str('empresa')}
              className="px-6 py-2.5 text-sm font-semibold text-gray-950 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
              {isLoading ? (
                <><span className="w-4 h-4 border-2 border-gray-950/30 border-t-gray-950 rounded-full animate-spin" /> Enviando...</>
              ) : '🚀 Gerar Diagnóstico'}
            </button>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={`text-center py-8 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
        NAI IT — Plataforma de GRC e Sustentabilidade · Dados protegidos por LGPD
      </div>

      {/* Honeypot */}
      <input type="text" name="website" value="" onChange={() => {}} style={{ display: 'none' }} aria-hidden="true" />
    </div>
  )
}
